<template>
  <div>
    <b-card>
      <b-card-body>
        <b-form ref="observer">
          <b-row>
            <b-col lg="4" md="4">
              <b-form-group>
                <PFormSelect
                  v-model="countrySelectedId"
                  :options="optionsCountry"
                  optionValue="id"
                  optionText="descripcion"
                  :label="$t('label.pais')"
                  :placeholder="$t('option.select')"
                  @input="resetTable"
                />
              </b-form-group>
            </b-col>
            <b-col lg="8" md="8">
              <b-row>
                <b-col lg="3" md="3" class="switchInput">
                  <PFormSwitch v-model="checkTransportadora" :label="$t('label.transportadora')" />
                </b-col>
                <b-col lg="3" md="3" class="switchInput">
                  <PFormSwitch
                    v-model="checkAprobacion"
                    :label="$t('label.aprobacionAutomatica')"
                  />
                </b-col>
                <b-col lg="3" md="3" class="switchInput">
                  <PFormSwitch v-model="checkTasaEnteros" :label="$t('label.tasaEnteros')" />
                </b-col>
                <b-col lg="3" md="3" class="switchInput">
                  <PFormSwitch
                    v-model="checkInformeComprasVentas"
                    :label="$t('label.informe.compras.ventas')"
                  />
                </b-col>
              </b-row>
              <b-row>
                <b-col lg="3" md="3" class="switchInput">
                  <PFormSwitch v-model="checkReportsLegacy" :label="$t('label.reportsLegacy')" />
                </b-col>

                <b-col lg="3" md="3" class="switchInput">
                  <PFormSwitch
                    v-model="checkDescargarVideos"
                    :label="$t('label.descargar.videos')"
                  />
                </b-col>

                <b-col lg="3" md="3" class="textIva">
                  <ValidationProvider
                    v-slot="{ errors }"
                    rules="decimal|min_value:0|max_value:100"
                    name="iva"
                  >
                    <p-form-input-text
                      style="width: 5rem"
                      id="input_text_iva"
                      v-model="inputTextIva"
                      :placeholder="$t('label.iva')"
                      type="number"
                      step="0.01"
                      :state="errors[0] ? false : null"
                      variant="primary"
                    />
                    <div v-if="errors[0]" class="text-danger">{{ errors[0] }}</div>
                  </ValidationProvider>
                </b-col>
                <b-col lg="3" md="3" class="switchInput">
                  <PFormSwitch
                    v-model="checkEditarOfertasDemandas"
                    :label="$t('label.editar.ofertas.demandas')"
                  />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-form>
        <br />
        <b-col class="float-right" lg="7" md="7">
          <b-button
            pill
            variant="outline-primary"
            class="p-button-secondary mr-4"
            @click="cancelar"
            >{{ $t("label.cancelar") }}</b-button
          >
          <b-button
            :disabled="!countrySelectedId"
            pill
            variant="primary"
            class="p-button-primary ml-4"
            @click="guardar"
            >{{ $t("label.guardar") }}</b-button
          >
        </b-col>
      </b-card-body>
    </b-card>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import Multiselect from "vue-multiselect";
import { Action, Getter } from "vuex-class";
import { IPaisParams, IPaisResponse } from "@/services";
import { IPais } from "@/entities";
import ColumnasTable from "@/components/administracion/columnas/ColumnasTable.vue";
import { PFormInputText, PFormSwitch } from "@/components/look";

@Component({
  components: {
    PFormSwitch,
    PFormInputText,
    ColumnasTable,
    ValidationObserver,
    Multiselect,
  },
})
export default class AdminPaisesComponent extends Vue {
  @Action("fetchPais")
  fetchPais!: (params?: IPaisParams) => Promise<IPaisResponse | undefined>;
  @Action("fetchClientesAll") fetchClientesAll!: () => Promise<void>;

  @Getter("getPaises") optionsCountry!: IPais[];

  private checkTransportadora: boolean | undefined = false;
  private checkAprobacion: boolean | undefined = false;
  private checkTasaEnteros: boolean | undefined = false;
  private checkInformeComprasVentas: boolean | undefined = false;
  private checkDescargarVideos: boolean | undefined = false;
  private checkReportsLegacy: boolean | undefined = false;
  private inputTextIva: number | null = null;
  private checkEditarOfertasDemandas: boolean | undefined = false;

  public countrySelected: IPais | null = null;
  public countrySelectedId: number | null = null;

  constructor() {
    super();
  }

  async resetTable(): Promise<void> {
    this.countrySelected =
      this.optionsCountry.find((pais) => pais.id === this.countrySelectedId) || null;
    if (this.countrySelected) {
      const rest = await this.fetchPais({
        codPais: this.countrySelected.codPais,
        codPaisCorto: this.countrySelected.codPaisCorto,
        codTimezone: this.countrySelected.codTimezone,
        id: this.countrySelected.id,
      });
      if (rest) {
        this.checkTransportadora = rest.content[0].hasTransportadora;
        this.checkAprobacion = rest.content[0].hasAprobacionAutomaticaOferta;
        this.checkTasaEnteros = rest.content[0].hasTasaEnteros;
        this.checkInformeComprasVentas = rest.content[0].hasInformeComprasVentas;
        this.checkDescargarVideos = rest.content[0].descargarVideos;
        this.checkReportsLegacy = rest.content[0].reportsLegacy;
        this.inputTextIva = rest.content[0].iva;
        this.checkEditarOfertasDemandas = rest.content[0].editOfertasDemandas;
      } else {
        this.checkTransportadora = false;
        this.checkAprobacion = false;
        this.checkTasaEnteros = false;
        this.checkInformeComprasVentas = false;
        this.checkDescargarVideos = false;
        this.checkReportsLegacy = false;
        this.inputTextIva = null;
        this.checkEditarOfertasDemandas = false;
      }
    }
  }

  cancelar(): void {
    if (this.countrySelected) {
      this.resetTable();
    } else {
      this.countrySelectedId = null;
      this.checkTransportadora = false;
      this.checkAprobacion = false;
      this.checkTasaEnteros = false;
      this.checkReportsLegacy = false;
      this.inputTextIva = null;
      this.checkEditarOfertasDemandas = false;
    }
  }

  guardar(): void {
    if (this.countrySelected) {
      this.$services.pais
        .saveConfPais({
          codPaisCorto: this.countrySelected.codPaisCorto,
          hasTransportadora: this.checkTransportadora || false,
          hasAprobacionAutomaticaOferta: this.checkAprobacion || false,
          hasTasaEnteros: this.checkTasaEnteros || false,
          hasInformeComprasVentas: this.checkInformeComprasVentas || false,
          descargarVideos: this.checkDescargarVideos || false,
          iva: this.inputTextIva || 0,
          hasReportsLegacy: this.checkReportsLegacy || false,
          editOfertasDemandas: this.checkEditarOfertasDemandas || false,
        })
        .then(() => {
          this.fetchClientesAll();
        });
    }
  }
}
</script>
<style lang="scss" scoped>
.switchInput {
  padding-top: 15px;
}
.textIva {
  padding-top: 15px;
}
</style>
